import React, { useState, useEffect } from "react";
import { ChevronDown, Save } from "lucide-react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import Toast from "../Toast/Toast";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import { useAuth } from "../../context/AuthContext";

const JournalEntry = ({ trade, userSettings, onError }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [journalContent, setJournalContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchJournalEntry = async () => {
      try {
        if (!user?.uid) return;

        console.log(`Fetching journal entry for trade ${trade.id}`);
        const journalRef = doc(db, "journalEntries", `${user.uid}_${trade.id}`);
        const journalSnap = await getDoc(journalRef);

        if (journalSnap.exists()) {
          console.log("Found journal entry:", journalSnap.data());
          setJournalContent(journalSnap.data().content);
        }
      } catch (error) {
        console.error("Error fetching journal entry:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJournalEntry();
  }, [trade.id, user?.uid]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculateAdjustedPnL = (trade) => {
    if (!userSettings?.useCommissions) return trade.pnl;
    const commissionCost = userSettings.commissionValue * trade.quantity * 2;
    return trade.pnl - commissionCost;
  };

  const getPreviewText = (text) => {
    if (!text) return "";
    // Remove HTML tags for preview
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    const plainText = tempDiv.textContent || tempDiv.innerText;
    return plainText.length > 100
      ? plainText.substring(0, 100) + "..."
      : plainText;
  };

  const calculateRRRatio = (reward, risk) => {
    if (!reward || !risk || risk === 0) return "";
    return `1:${(reward / risk).toFixed(2)}`;
  };

  const handleSave = async () => {
    try {
      if (!user?.uid) {
        throw new Error("User ID is required");
      }

      console.log("Saving journal content:", journalContent);

      const journalRef = doc(db, "journalEntries", `${user.uid}_${trade.id}`);
      await setDoc(
        journalRef,
        {
          tradeId: trade.id,
          userId: user.uid,
          content: journalContent || "",
          symbol: trade.symbol,
          tradeDate: trade.date,
          tags: trade.tags || [],
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Verify save
      const verification = await getDoc(journalRef);
      console.log("Verification of saved data:", verification.data());

      setToast({
        type: "success",
        message: "Journal Entry Saved",
        subText: `Journal entry for ${trade.symbol} has been saved successfully.`,
      });
    } catch (error) {
      console.error("Error saving journal entry:", error);
      console.error("Attempted content:", journalContent);
      setToast({
        type: "error",
        message: "Error Saving Entry",
        subText: error.message || "Please try again",
      });
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="trade-entry">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="trade-header" onClick={() => setExpanded(!expanded)}>
        <div className="trade-info-column">
          <div className="trade-info-main">
            <span className="trade-symbol">{trade.symbol}</span>
            <span className="trade-time">
              {new Date(trade.date).toLocaleTimeString()}
            </span>
          </div>
          {trade.tags && trade.tags.length > 0 && (
            <div className="tags-container">
              {trade.tags.map((tag, index) => (
                <div key={index} className="tag">
                  <span
                    className="tag-color"
                    style={{ backgroundColor: tag.color }}
                  ></span>
                  <span className="tag-text">{tag.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="trade-summary">
          <span
            className={`trade-pnl ${
              calculateAdjustedPnL(trade) >= 0 ? "profit" : "loss"
            }`}
          >
            {formatCurrency(calculateAdjustedPnL(trade))}
          </span>
          <ChevronDown
            size={20}
            className={`expand-arrow ${expanded ? "expanded" : ""}`}
          />
        </div>
      </div>

      {!expanded && journalContent && (
        <div className="preview-content" onClick={() => setExpanded(true)}>
          <div className="journal-preview">
            {getPreviewText(journalContent)}
          </div>
        </div>
      )}

      {expanded && (
        <div className="journal-content" onClick={(e) => e.stopPropagation()}>
          <div className="trade-details-grid">
            <div className="trade-detail">
              <span className="detail-label">Entry Price</span>
              <span className="detail-value">
                {formatCurrency(trade.entryPrice)}
              </span>
            </div>
            <div className="trade-detail">
              <span className="detail-label">Exit Price</span>
              <span className="detail-value">
                {formatCurrency(trade.exitPrice)}
              </span>
            </div>
            <div className="trade-detail">
              <span className="detail-label">Quantity</span>
              <span className="detail-value">{trade.quantity}</span>
            </div>
            <div className="trade-detail">
              <span className="detail-label">P&L</span>
              <span
                className={`detail-value ${
                  calculateAdjustedPnL(trade) >= 0 ? "profit" : "loss"
                }`}
              >
                {formatCurrency(calculateAdjustedPnL(trade))}
              </span>
            </div>
            {userSettings?.useCommissions && (
              <div className="trade-detail">
                <span className="detail-label">Commissions</span>
                <span className="detail-value">
                  {formatCurrency(
                    userSettings.commissionValue * trade.quantity * 2
                  )}
                </span>
              </div>
            )}
            {trade.setup && (
              <div className="trade-detail">
                <span className="detail-label">Setup</span>
                <span className="detail-value">{trade.setup}</span>
              </div>
            )}
            {trade.risk && trade.reward && (
              <div className="trade-detail">
                <span className="detail-label">R:R</span>
                <span className="detail-value">
                  {calculateRRRatio(trade.reward, trade.risk)}
                </span>
              </div>
            )}
          </div>

          <RichTextEditor
            content={journalContent}
            onChange={setJournalContent}
          />

          <button className="save-button" onClick={handleSave}>
            <Save size={16} />
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default JournalEntry;
